import {DiceState, CurrentState, TradeState, NestedReadonly, GameState, NestedPartial, CardEnum, PlayerColor, TradeResponseStatus, ActionState} from '@colonist/game-states'
import {UIGameState} from './UIGameState'
import {BuildGameConfigurations, GameDetails, GameSettings} from '@colonist/game-base-types'
import {ArrayUtils} from '@colonist/utils'
import {TranslationObject} from '@colonist/i18n'
import {getEmptyPlayerState} from '@colonist/game-state-types'
import {UIGameManager} from './UIGameManager'
import {BankValidator, CurrentStateValidator, GameValidator, MapValidator, MechanicValidatorDevelopmentCard, MechanicValidatorProgressCards, PlayerValidator, TradeBankRatio} from '@colonist/game-mechanic-validators'
import {DeviceType, LobbyImage} from '@colonist/types'
import {CA} from '../analytics/Analytics'

// TODO rename this class later
export class UIGameController {
    private readonly stateController: UIGameState
    readonly gameDetails: GameDetails
    readonly gameSettings: GameSettings

    // !Warning. All states in this class should be wrapper by `NestedReadonly`
    // !Warning. This includes all states exported by getters
    readonly gameValidator: GameValidator
    readonly diceState: NestedReadonly<DiceState>
    readonly mapValidator: MapValidator
    readonly bankState: BankValidator
    readonly currentStateValidator: CurrentStateValidator
    readonly playOrder: PlayerColor[]
    readonly tradeState: NestedReadonly<TradeState>

    private readonly playerValidators: Map<PlayerColor, PlayerValidator>
    private readonly devCardsValidator?: MechanicValidatorDevelopmentCard
    private readonly progressCardsValidator?: MechanicValidatorProgressCards

    myColor: PlayerColor
    timeLeftInCurrentState: number

    private readonly uiGameManager: UIGameManager
    readonly isReplay: boolean

    constructor(uiGameManager: UIGameManager, configurations: BuildGameConfigurations) {
        this.uiGameManager = uiGameManager
        this.stateController = new UIGameState(configurations.gameState)
        this.gameDetails = configurations.gameDetails
        this.gameSettings = GameSettings.deserialize(configurations.gameSettings)
        this.uiGameManager.gameState.initGameModeData(configurations.gameSettings.modeSetting)
        this.timeLeftInCurrentState = configurations.timeLeftInState

        this.gameValidator = new GameValidator(configurations.gameState, configurations.playerUserStates)
        this.diceState = configurations.gameState.diceState
        this.mapValidator = new MapValidator(configurations.gameState.mapState)
        this.bankState = new BankValidator(configurations.gameState.bankState)

        const experimentalCurrentState = configurations.gameState.experimentalMechanicState?.currentState
        const mechanicTurnColonistRushState = configurations.gameState.experimentalMechanicState?.mechanicTurnColonistRushState
        const mechanicRobberColonistRushState = configurations.gameState.experimentalMechanicState?.mechanicRobberColonistRushState
        const playerStates = configurations.gameState.experimentalMechanicState?.playerStates
        const playerColors = configurations.playerUserStates.map((userState) => userState.selectedColor)
        this.currentStateValidator = new CurrentStateValidator(configurations.gameState.currentState, experimentalCurrentState, mechanicTurnColonistRushState, playerStates, playerColors, mechanicRobberColonistRushState)

        this.playerValidators = new Map()
        this.uiGameManager.gameState.players = []
        for(const playerColorKey in configurations.gameState.playerStates) {
            const playerColor = Number(playerColorKey) as PlayerColor

            const playerState = configurations.gameState.playerStates[playerColor]
            if(playerState == undefined) continue

            const userState = configurations.playerUserStates.find((playerUserState) => playerUserState.selectedColor === playerColor)
            if(userState == undefined) continue

            const playerStateController = new PlayerValidator(playerState, userState)
            this.playerValidators.set(playerStateController.color, playerStateController)
            this.uiGameManager.gameState.initPlayerControllerStates(playerState, userState)
        }
        this.playOrder = configurations.playOrder
        this.myColor = configurations.playerColor

        const {mechanicDevelopmentCardsState, mechanicProgressCardsState} = configurations.gameState
        if(mechanicDevelopmentCardsState) this.devCardsValidator = new MechanicValidatorDevelopmentCard(this.getPlayerValidators(), this.mapValidator, this.currentStateValidator, mechanicDevelopmentCardsState)
        if(mechanicProgressCardsState) this.progressCardsValidator = new MechanicValidatorProgressCards(this.getPlayerValidators(), this.mapValidator, mechanicProgressCardsState)

        this.tradeState = configurations.gameState.tradeState
        this.isReplay = configurations.isReplay ?? false
        uiGameManager.gameState.initMapState(this.mapValidator)

        void CA.sessionRecorder.conditionallyStartRecording(this.gameSettings.modeSetting)
    }

    get currentState(): NestedReadonly<CurrentState> {
        return this.currentStateValidator.currentState
    }

    applyStateUpdates(stateUpdate: NestedPartial<GameState>[]) {
        this.stateController.applyStateUpdates(stateUpdate)
    }

    botSessionCount(): number {
        let amount = 0
        for(const player of this.playerValidators.values()) {
            if(player.isUser) continue
            amount += 1
        }
        return amount
    }

    getPlayerState(player: PlayerColor): PlayerValidator {
        const isPlayerSpectator = player == PlayerColor.None
        if(isPlayerSpectator) {
            return new PlayerValidator(getEmptyPlayerState(), {
                userId: undefined,
                username: '',
                databaseIcon: LobbyImage.Bot,
                isBot: false,
                deviceType: DeviceType.Unknown,
                countryCode: undefined,
                regionUpdated: undefined,
                membership: undefined,
                selectedColor: PlayerColor.None,
                profilePictureUrl: undefined,
            })
        }

        const playerValidator = this.playerValidators.get(player)

        if(playerValidator != undefined) return playerValidator
        throw new Error('no player found: ' + PlayerColor[player])
    }

    getPlayerValidators(): PlayerValidator[] {
        const playerValidators: PlayerValidator[] = []
        for(const [playerColor, playerControllerState] of this.playerValidators) {
            playerValidators.push(playerControllerState)
        }
        return playerValidators
    }

    getPlayerValidatorsMap(): Map<PlayerColor, PlayerValidator> {
        return this.playerValidators
    }

    amITakingAction(): boolean {
        // Returns true if I am taking action or if no one else is taking action and it is my turn.
        return this.gameValidator.getPlayersTakingAction().includes(this.myColor)
    }

    wasATradeAccepted(): boolean {
        for(const activeTradeState of Object.values(this.tradeState.activeOffers)) {
            if(activeTradeState == undefined) continue

            for(const player of Object.keys(activeTradeState.playerResponses)) {
                const playerColor = Number(player) as PlayerColor

                if(activeTradeState.playerResponses[playerColor] == TradeResponseStatus.Accepted) return true
            }
        }
        return false
    }

    getPlayerColors(): PlayerColor[] {
        return [...this.playerValidators.keys()]
    }

    get isMyTurn(): boolean {
        if(this.currentStateValidator == undefined) return false
        return this.currentStateValidator.currentTurnPlayers.includes(this.myColor)
    }

    get myActionState(): ActionState {
        if(this.currentStateValidator == undefined) return this.currentState.actionState
        return this.currentStateValidator.getPlayerActionState(this.myColor)
    }

    get isPlayerSpectator(): boolean {
        if(this.myColor == PlayerColor.None) return true
        return false
    }

    getAllCardsInHand(player: PlayerColor): CardEnum[] {
        const allCards: CardEnum[] = []
        allCards.push(...this.getPlayerState(player).resourceCards.cards)

        const devCards = this.devCardsValidator?.getDevelopmentCardsPlayerState(player).developmentCards
        if(devCards) allCards.push(...devCards.cards)

        const progressCards = this.progressCardsValidator?.getProgressCardsPlayerValidator(player).progressCards
        if(progressCards) allCards.push(...progressCards.cards)
        return allCards
    }

    totalResourceCardOfType(player: PlayerColor, cardEnum: CardEnum): number {
        return ArrayUtils.countOccurenceOfValueInArray(this.getAllCardsInHand(player), cardEnum)
    }

    getPlayerNameWithColor(color: PlayerColor): TranslationObject {
        return {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.getPlayerState(color).username}}
    }

    getPlayerBankTradeRatios(player: PlayerColor): TradeBankRatio[] {
        if(player == PlayerColor.None) return []
        const playerState = this.getPlayerState(player)
        const bankTradeRatiosState = playerState.bankTradeRatiosState
        const bankTradeRatiosArray = Object.entries(bankTradeRatiosState).map(([resource, tradeRatio]) => ({
            resource: resource as unknown as CardEnum,
            tradeRatio: tradeRatio,
        }))
        return bankTradeRatiosArray
    }

    is1v1(): boolean {
        return this.playOrder.length == 2
    }

    isBotGame(): boolean {
        return this.botSessionCount() == this.playOrder.length - 1
    }
}
